import { Icon } from '../../components/Icon';
import type { IconName } from '../../components/Icon';
import { localDateISO, whereIsAt } from '../../domain/places';
import type { PersonWithNames } from '../../domain/types';
import { useI18n } from '../../i18n';
import { whatsappLink } from '../../lib/phone';
import { facebookLink, instagramLink } from '../../lib/social';
import { useApp } from '../../app/AppState';

/**
 * How to reach a relative, in as few taps as the family already knows.
 *
 * A message on WhatsApp is the one way that suits every relative and every
 * hour, so it comes first and it is the only one that is ever certain to be
 * there. Beside it — and only when that person typed them in — are the two
 * places this family actually looks for each other afterwards: Instagram and
 * Facebook. Calling and email were here once and are deliberately gone; they
 * made the row a decision rather than a tap, and every number that can be
 * called can be messaged.
 *
 * Nothing is ever advertised on somebody's behalf. A button exists only for a
 * detail they typed themselves, on a living circle that is not your own, and
 * only while they have sharing switched on — which is the same rule the phone
 * number has always followed.
 *
 * The marks are the real ones, in the real colours. Everywhere else this app
 * draws its own icons, but somebody hunting for "the WhatsApp button" is
 * hunting for the green one they know from their phone, and a tasteful outline
 * of a speech bubble is a worse button for being ours.
 */
export function ReachButtons({ person, className = '', labels = false }: { person: PersonWithNames; className?: string; /** Text beside the icon (profile); the card keeps a round icon with a tooltip. */ labels?: boolean }) {
  const { t } = useI18n();
  const { me, places, locationsById, now, shabbatIds } = useApp();
  if (me?.id === person.id || person.status === 'deceased') return null;
  const c = person.contact;
  if (!c?.shared) return null;
  const where = whereIsAt(person, places, localDateISO(now));
  const home = person.homeLocationId ? locationsById.get(person.homeLocationId) : where ? locationsById.get(where.locationId) : undefined;
  const shabbat = shabbatIds.has(person.id);
  const hint = shabbat ? ` · ${t('reach.shabbatHint')}` : '';

  /*
   * One list, in one order, everywhere the row appears: a message, then where
   * they can be found. The links are built by `lib/phone` and `lib/social`,
   * which are the things that know how to turn what somebody typed — a local
   * number, an @handle, a pasted share-sheet URL — into an address that opens.
   * Anything they cannot make sense of simply has no button, which is the
   * quietest way to fail: nothing is said about a detail nobody can use.
   */
  const links: Array<{ key: string; href: string; icon: IconName; label: string; word: string }> = [];
  const wa = whatsappLink(c.phone, home?.countryCode);
  // Only the message carries the Shabbat hint: a profile page is not a door anyone knocks on.
  if (wa) links.push({ key: 'whatsapp', href: wa, icon: 'whatsapp', label: `${t('reach.message')}${hint}`, word: t('reach.whatsapp') });
  const ig = instagramLink(c.instagram);
  if (ig) links.push({ key: 'instagram', href: ig, icon: 'instagram', label: t('reach.instagramOpen'), word: t('reach.instagram') });
  const fb = facebookLink(c.facebook);
  if (fb) links.push({ key: 'facebook', href: fb, icon: 'facebook', label: t('reach.facebookOpen'), word: t('reach.facebook') });
  if (links.length === 0) return null;

  const cls = labels ? 'btn btn-sm btn-secondary' : 'fab-icon';
  return (
    <span className={`reach-buttons ${labels ? 'has-labels' : ''} ${shabbat ? 'is-shabbat' : ''} ${className}`}>
      {links.map((l) => (
        <a key={l.key} href={l.href} target="_blank" rel="noopener noreferrer" className={`${cls} reach-${l.key}`} aria-label={l.label} title={l.label}>
          <Icon name={l.icon} />
          {labels && <span>{l.word}</span>}
        </a>
      ))}
      {/* While it is Shabbat where they are, the message dims and says why in its own label — a
          separate sentence beside it was a line of explanation for a state that is usually off. */}
    </span>
  );
}
